import { useCallback, useState } from 'react';
import type { JSX } from 'react';

import { Helmet } from 'react-helmet-async';

import { stripHtmlTags } from '@change-corgi/core/html';
import { useI18n } from '@change-corgi/core/react/i18n';
import { ClientRender } from '@change-corgi/core/react/ssr/render';
import { Container } from '@change-corgi/design-system-legacy/layout';

import { useCurrentUser } from 'src/app/shared/hooks/session';

import { AboutPanel } from '../components/AboutPanel';
import { AdminToolbar } from '../components/AdminToolbar';
import { ColumnSection } from '../components/ColumnSection';
import { HeadingPanel } from '../components/HeadingPanel';
import { SharePanel } from '../components/SharePanel';
import { useMovementViewTracking } from '../hooks/tracking/useMovementTracking';
import { useMovementContext } from '../hooks/useMovementContext';

import { MovementPetitionsContainer } from './MovementPetitionsContainer';

export function MovementContainer(): JSX.Element {
	useMovementViewTracking();

	const user = useCurrentUser();
	const { localizeNumber } = useI18n();

	const initialMovementInfo = useMovementContext();

	const [movementInfo, setMovementInfo] = useState(() => initialMovementInfo);
	const {
		id: movementId,
		description,
		objective,
		uniqueSignatureCount,
		uniqueSignatureCountLocalized,
		photo,
		petitionCount,
		petitionCountLocalized,
	} = movementInfo;
	const mediaUrl = photo?.petitionMedium.url;

	const onPetitionRemoved = useCallback(() => {
		setMovementInfo({
			...movementInfo,
			petitionCount: movementInfo.petitionCount - 1,
			petitionCountLocalized: localizeNumber(movementInfo.petitionCount - 1),
		});
	}, [movementInfo, localizeNumber]);

	return (
		<>
			<Helmet>
				<title>{`${objective} · Movement`}</title>
				<meta name="description" content={stripHtmlTags(description)} />
			</Helmet>
			{user?.roles.staff && <AdminToolbar movementId={movementId} />}
			<article>
				<Container my={[16, 32]} px={8}>
					<ColumnSection>
						<HeadingPanel
							petitionTitle={objective}
							petitionCount={petitionCount}
							petitionCountLocalized={petitionCountLocalized}
							supporterCount={uniqueSignatureCount}
							supporterCountLocalized={uniqueSignatureCountLocalized}
						/>
						<SharePanel movementId={movementId} objective={objective} />
					</ColumnSection>
					<ColumnSection>
						<AboutPanel movementObjective={objective} movementDetails={description} mediaUrl={mediaUrl} />
					</ColumnSection>
					<ColumnSection>
						<ClientRender>
							<MovementPetitionsContainer movementId={movementId} onPetitionRemoved={onPetitionRemoved} />
						</ClientRender>
					</ColumnSection>
				</Container>
			</article>
		</>
	);
}
